"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import {
  Search,
  ChevronDown,
  ChevronUp,
  X,
  Plus,
  Briefcase,
} from "lucide-react";
import ApplyButton from "../common/ApplyButton";
import { useApplicationStatus } from "../context/ApplicationStatusContext";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CareerPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("openings");
  const [jobListings, setJobListings] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [newExperience, setNewExperience] = useState({
    title: "",
    company: "",
    years: "",
  });
  const [applicationForm, setApplicationForm] = useState({
    name: "",
    email: "",
    department: "",
    cv: null,
  });
  const [openJobId, setOpenJobId] = useState(null);
  const { isApplicationOpen } = useApplicationStatus();

  useEffect(() => {
    fetchJobListings();
  }, []);

  const fetchJobListings = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/careers/job-listings`
      );
      const updatedJobListings = response.data.map((job) => {
        let requirements = [];
        try {
          requirements = JSON.parse(job.requirements);
        } catch (error) {
          console.error(`Error parsing requirements for job ${job.id}`, error);
        }
        return { ...job, requirements };
      });
      setJobListings(updatedJobListings);
    } catch (error) {
      console.error("Error fetching job listings:", error);
      toast.error("Failed to load job listings. Please try again.");
    }
  };

  const toggleJob = (id) => {
    setOpenJobId(openJobId === id ? null : id);
  };

  const handleExperienceChange = (e) => {
    setNewExperience({ ...newExperience, [e.target.name]: e.target.value });
  };

  const addExperience = () => {
    if (newExperience.title && newExperience.company && newExperience.years) {
      setExperiences([...experiences, newExperience]);
      setNewExperience({ title: "", company: "", years: "" });
    }
  };

  const removeExperience = (index) => {
    setExperiences(experiences.filter((_, i) => i !== index));
  };

  const handleApplicationChange = (e) => {
    if (e.target.name === "file") {
      setApplicationForm({ ...applicationForm, cv: e.target.files[0] });
    } else {
      setApplicationForm({
        ...applicationForm,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();

    if (!isApplicationOpen) {
      toast.warning("Applications are currently closed.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("name", applicationForm.name);
      formData.append("email", applicationForm.email);
      formData.append("department", applicationForm.department);
      formData.append("experiences", JSON.stringify(experiences));
      formData.append("cv", applicationForm.cv);
      formData.append("jobId", openJobId);

      await api.post("/careers/apply", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Application submitted successfully!");
      // Reset form
      setApplicationForm({
        name: "",
        email: "",
        department: "",
        cv: null,
      });
      setExperiences([]);
      document.getElementById("cv").value = "";
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application. Please try again.");
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
      />

      {/* Highlighted Professional Home Button */}
      <div className="fixed bottom-8 right-8 z-50">
        <a
          href="/"
          className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full shadow-lg hover:shadow-blue-400/50 hover:scale-110 transition-all duration-300 border-2 border-white group"
          aria-label="Return to home page"
        >
          <div className="relative">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-7 h-7 text-white group-hover:animate-pulse"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
            <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-blue-700 text-white text-xs font-bold py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
              Home Page
            </span>
          </div>
        </a>
      </div>

      {/* Hero Section */}
      <div className="bg-blue-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Join Our Team</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Discover career opportunities at NIIT and be part of our mission to
            provide quality education and innovation.
          </p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <nav className="bg-white shadow-md">
        <div className="container mx-auto px-4">
          <ul className="flex flex-wrap text-sm font-medium">
            <li className="mr-6">
              <button
                className={`py-4 px-2 ${
                  activeTab === "openings"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab("openings")}
              >
                Job Openings
              </button>
            </li>
            <li className="mr-6">
              <button
                className={`py-4 px-2 ${
                  activeTab === "apply"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-500"
                }`}
                onClick={() => setActiveTab("apply")}
              >
                Apply Now
              </button>
            </li>
          </ul>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-8">
        {activeTab === "openings" && (
          <div>
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search for jobs..."
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Search
                  className="absolute left-3 top-2.5 text-gray-400"
                  size={20}
                />
              </div>
            </div>

            <div className="space-y-6">
              {jobListings.map((job) => (
                <div key={job.id} className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-2xl font-semibold mb-2">{job.title}</h2>
                  <p className="text-gray-600 mb-4">
                    {job.department} • {job.type}
                  </p>
                  <p className="text-gray-700 mb-4">{job.description}</p>
                  <h3 className="font-semibold mb-2">Requirements:</h3>
                  <ul className="list-disc pl-5 mb-4">
                    {job.requirements.map((req, index) => (
                      <li key={index} className="text-gray-700">
                        {req}
                      </li>
                    ))}
                  </ul>

                  <div className="flex justify-between items-center mt-6">
                    <button
                      onClick={() => toggleJob(job.id)}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      {openJobId === job.id ? (
                        <>
                          <ChevronUp size={20} className="mr-1" /> Less Details
                        </>
                      ) : (
                        <>
                          <ChevronDown size={20} className="mr-1" /> More
                          Details
                        </>
                      )}
                    </button>
                    <ApplyButton
                      onClick={() => {
                        setActiveTab("apply");
                        setApplicationForm({
                          ...applicationForm,
                          department: job.department,
                        });
                        setOpenJobId(job.id);
                      }}
                    >
                      Apply Now
                    </ApplyButton>
                  </div>

                  {openJobId === job.id && (
                    <div className="mt-4 pt-4 border-t">
                      <h3 className="font-semibold mb-2">Responsibilities:</h3>
                      <ul className="list-disc pl-5 mb-4">
                        {job.responsibilities.map((resp, index) => (
                          <li key={index} className="text-gray-700">
                            {resp}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "apply" && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <Briefcase className="mr-2" /> Career Application
            </h2>
            <form onSubmit={handleSubmitApplication}>
              <div className="mb-4">
                <label
                  htmlFor="name"
                  className="block text-gray-700 font-bold mb-2"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={applicationForm.name}
                  onChange={handleApplicationChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="email"
                  className="block text-gray-700 font-bold mb-2"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={applicationForm.email}
                  onChange={handleApplicationChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  htmlFor="department"
                  className="block text-gray-700 font-bold mb-2"
                >
                  Department
                </label>
                <select
                  id="department"
                  name="department"
                  value={applicationForm.department}
                  onChange={handleApplicationChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Department</option>
                  <option value="Computer Science">Computer Science</option>
                  <option value="Artificial Intelligence">
                    Artificial Intelligence
                  </option>
                  <option value="Engineering">Engineering</option>
                  <option value="Mathematics">Mathematics</option>
                  <option value="Administration">Administration</option>
                </select>
              </div>

              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-gray-700 font-bold">
                    Work Experience
                  </label>
                  {experiences.length > 0 && (
                    <span className="text-sm text-gray-500">
                      {experiences.length} experience(s) added
                    </span>
                  )}
                </div>

                {experiences.map((exp, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 p-4 rounded-lg mb-2 flex justify-between items-center"
                  >
                    <div>
                      <p className="font-medium">{exp.title}</p>
                      <p className="text-sm text-gray-600">
                        {exp.company} • {exp.years} years
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeExperience(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ))}

                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      name="title"
                      placeholder="Job Title"
                      value={newExperience.title}
                      onChange={handleExperienceChange}
                      className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      name="company"
                      placeholder="Company"
                      value={newExperience.company}
                      onChange={handleExperienceChange}
                      className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex">
                    <input
                      type="number"
                      name="years"
                      placeholder="Years of Experience"
                      value={newExperience.years}
                      onChange={handleExperienceChange}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="0"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addExperience}
                    className="w-full flex justify-center items-center bg-gray-200 text-gray-800 py-2 px-4 rounded hover:bg-gray-300 transition-colors"
                  >
                    <Plus size={20} className="mr-2" /> Add Experience
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="cv"
                  className="block text-gray-700 font-bold mb-2"
                >
                  Upload CV
                </label>
                <input
                  type="file"
                  id="cv"
                  name="file"
                  onChange={handleApplicationChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  accept=".pdf,.doc,.docx"
                />
              </div>

              <ApplyButton
                type="submit"
                className="w-full py-3"
                onClick={handleSubmitApplication}
              >
                Submit Application
              </ApplyButton>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default CareerPage;
